import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { ChannelDot } from '../components/ui/Channel'
import { Icon, I } from '../components/ui/icons'
import { Field, MOBILE_FULL_WIDTH_BUTTON_STYLE as COMPACT_FULL_WIDTH_BUTTON_STYLE, MOBILE_SINGLE_COLUMN, TEXT_WRAP_STYLE, gridColumns, mobileButtonStyle } from '../components/ui'
import { Pill } from '../components/ui/Pill'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  createChannelConnection,
  deleteChannelConnection,
  enqueueChannelOrderPullJob,
  enqueueChannelProductPullJob,
  getChannelWebhookStatus,
  listChannelConnections,
  listChannelProviders,
  listLatestChannelOnboardingJobs,
  listLatestChannelOrderPullJobs,
  listLatestChannelProductPullJobs,
  registerChannelWebhooks,
  retryChannelOnboardingJob,
  retryChannelProductPullJob,
  startChannelConnect,
  storeChannelCredentials,
} from '../channels/channelsApi'
import type {
  ChannelConnection,
  ChannelConnectionStatus,
  ChannelOnboardingJob,
  ChannelOnboardingJobStatus,
  ChannelOrderPullJob,
  ChannelPullOrdersParams,
  ChannelProductPullJob,
  ChannelProductPullJobStatus,
  ChannelProvider,
  ChannelProviderOption,
  ChannelWebhookStatus,
} from '../channels/types'
import { getNoonSalesImportCapability } from '../channels/noonSalesImport/noonSalesImportApi'
import type { NoonSalesImportCapability } from '../channels/noonSalesImport/types'
import { NoonSalesImportPanel } from './NoonSalesImportPanel'
import { NoonCatalogImportPanel } from './NoonCatalogImportPanel'
import { useSharedImports } from '../channels/importsContext'
import { listSourceAuthorities, updateSourceAuthority } from '../source/sourceApi'
import type { SourceAuthority, SourceOwnerType } from '../source/types'
import { listStockAllocationRuns } from '../stockAllocation/stockAllocationApi'
import type { StockAllocationRunSummary } from '../stockAllocation/types'

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'
type SourceChoice = 'KEEP_EXISTING' | 'TLAAQ' | 'CHANNEL'
type MutableSourceChoice = 'TLAAQ' | 'CHANNEL'
type PullChoice = 'BOOTSTRAP' | 'LISTINGS_ONLY'

interface FormState {
  provider: ChannelProvider | ''
  displayName: string
}

interface CredentialFormState {
  sellerId: string
  apiKey: string
  apiSecret: string
  userAgent: string
  refreshToken: string
  marketplaceIds: string
  region: string
  serviceAccountJson: string
  warehouseCode: string
}

interface OrderPullFormState {
  dateFrom: string
  dateTo: string
}

interface ChannelsPageProps {
  accessToken: string
  onOpenConnection?: (channelConnectionId: number) => void
  onReviewUnmapped?: (channelConnectionId: number) => void
  onOpenStockAllocation?: (tab?: 'rules' | 'activity') => void
}

const EMPTY_FORM: FormState = { provider: '', displayName: '' }
const EMPTY_CREDENTIAL_FORM: CredentialFormState = { sellerId: '', apiKey: '', apiSecret: '', userAgent: '', refreshToken: '', marketplaceIds: '', region: 'EU', serviceAccountJson: '', warehouseCode: '' }
const EMPTY_ORDER_PULL_FORM: OrderPullFormState = { dateFrom: '', dateTo: '' }
const DISMISSED_ORDER_PULL_JOBS_STORAGE_KEY = 'tlaaq_dismissed_order_pull_jobs'
const DISMISSED_PRODUCT_PULL_JOBS_STORAGE_KEY = 'tlaaq_dismissed_product_pull_jobs'
const MAX_DISMISSED_ORDER_PULL_JOBS = 100
const MAX_DISMISSED_PRODUCT_PULL_JOBS = 100

const PROVIDER_DOT_NAMES: Record<ChannelProvider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function statusTone(status: ChannelConnectionStatus): PillTone {
  if (status === 'CONNECTED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'AUTH_EXPIRED' || status === 'ERROR') return 'danger'
  return 'neutral'
}

function onboardingJobTone(status: ChannelOnboardingJobStatus): PillTone {
  if (status === 'COMPLETED') return 'success'
  if (status === 'COMPLETED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  return 'info'
}

function isOnboardingJobActive(job: ChannelOnboardingJob): boolean {
  return job.status === 'QUEUED' || job.status === 'RUNNING'
}

function isOrderPullJobActive(job: ChannelOrderPullJob): boolean {
  return job.status === 'QUEUED' || job.status === 'RUNNING'
}

function loadDismissedOrderPullJobIds(): Set<number> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(DISMISSED_ORDER_PULL_JOBS_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(stored)) return new Set()
    return new Set(stored.filter((value): value is number => typeof value === 'number' && Number.isInteger(value)))
  } catch {
    return new Set()
  }
}

function loadDismissedProductPullJobIds(): Set<number> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(DISMISSED_PRODUCT_PULL_JOBS_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(stored)) return new Set()
    return new Set(stored.filter((value): value is number => typeof value === 'number' && Number.isInteger(value)))
  } catch {
    return new Set()
  }
}

function orderPullDateParam(value: string, boundary: 'start' | 'end'): string | null {
  if (!value) return null
  return boundary === 'start' ? `${value}T00:00:00.000Z` : `${value}T23:59:59.999Z`
}

function buildOrderPullParams(form: OrderPullFormState): ChannelPullOrdersParams {
  if (form.dateFrom && form.dateTo && form.dateTo < form.dateFrom) {
    throw new ApiClientError(400, { code: 'CHANNEL_INVALID_DATE_RANGE' })
  }
  return {
    dateFrom: orderPullDateParam(form.dateFrom, 'start'),
    dateTo: orderPullDateParam(form.dateTo, 'end'),
  }
}

const NOON_SERVICE_ACCOUNT_REQUIRED_FIELDS = ['private_key', 'key_id', 'project_code', 'channel_identifier'] as const

function validateNoonServiceAccountJson(value: string): string | null {
  if (!value.trim()) {
    return 'CHANNEL_CREDENTIALS_REQUIRED'
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    return 'CHANNEL_CREDENTIALS_JSON_INVALID'
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return 'CHANNEL_CREDENTIALS_JSON_INVALID'
  }

  const record = parsed as Record<string, unknown>
  const missingRequiredField = NOON_SERVICE_ACCOUNT_REQUIRED_FIELDS.some(field => {
    const fieldValue = record[field]
    return typeof fieldValue !== 'string' || !fieldValue.trim()
  })
  return missingRequiredField ? 'CHANNEL_CREDENTIALS_JSON_FIELDS_REQUIRED' : null
}

function mergeLatestOrderPullJobs(serverJobs: ChannelOrderPullJob[], currentJobs: ChannelOrderPullJob[]): ChannelOrderPullJob[] {
  const serverConnectionIds = new Set(serverJobs.map(job => job.channelConnectionId))
  const optimisticActiveJobs = currentJobs.filter(job => isOrderPullJobActive(job) && !serverConnectionIds.has(job.channelConnectionId))
  return [...serverJobs, ...optimisticActiveJobs]
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `channels.errors.codes.${error.code}`
  }
  return 'channels.errors.codes.REQUEST_FAILED'
}

function sameOwner(authority: SourceAuthority | undefined, ownerType: SourceOwnerType, channelConnectionId?: number): boolean {
  if (!authority) return ownerType === 'TLAAQ'
  if (ownerType === 'TLAAQ') return authority.ownerType === 'TLAAQ'
  return authority.ownerType === ownerType && authority.channelConnectionId === channelConnectionId
}

function isInventorySource(authority: SourceAuthority | null | undefined): boolean {
  return authority?.ownerType === 'INVENTORY_CONNECTION'
}

function supportsWebhookManagement(connection: ChannelConnection): boolean {
  return (connection.provider === 'ZID' || connection.provider === 'TRENDYOL' || connection.provider === 'SALLA') && connection.status === 'CONNECTED'
}

function supportsManualCredentials(connection: ChannelConnection): boolean {
  return (connection.provider === 'TRENDYOL' || connection.provider === 'AMAZON' || connection.provider === 'NOON') && connection.adapterAvailable
}

function supportsStockSource(connection: ChannelConnection): boolean {
  return connection.capabilities?.inventoryPull.enabled ?? (connection.provider !== 'AMAZON' && connection.provider !== 'NOON')
}

function supportsManualOrderPull(connection: ChannelConnection): boolean {
  return connection.capabilities?.orderPull.enabled
    ?? (connection.provider === 'ZID' || connection.provider === 'TRENDYOL' || connection.provider === 'SALLA' || connection.provider === 'AMAZON')
}

function noonWebhookSetup(status: ChannelWebhookStatus | undefined) {
  const subscription = status?.registeredSubscriptions.find(item => item.targetUrl || item.apiKey)
    ?? status?.expected.find(item => item.targetUrl || item.apiKey)
    ?? status?.missingSubscriptions.find(item => item.targetUrl || item.apiKey)
  return {
    targetUrl: subscription?.targetUrl ?? null,
    apiKey: subscription?.apiKey ?? null,
  }
}

function hasNoonWebhookSetup(status: ChannelWebhookStatus | undefined): boolean {
  const setup = noonWebhookSetup(status)
  return Boolean(setup.targetUrl || setup.apiKey)
}

function shouldFetchNoonWebhookStatus(connection: ChannelConnection, status: ChannelWebhookStatus | undefined): boolean {
  return connection.provider === 'NOON' && connection.status === 'CONNECTED' && !hasNoonWebhookSetup(status)
}

function webhookStatusesFromConnections(connections: ChannelConnection[]): Record<number, ChannelWebhookStatus> {
  return connections.reduce<Record<number, ChannelWebhookStatus>>((acc, connection) => {
    if (connection.webhookStatus) {
      acc[connection.id] = connection.webhookStatus
    }
    return acc
  }, {})
}

function mergeWebhookStatus(connection: ChannelConnection, status: ChannelWebhookStatus): ChannelConnection {
  return connection.id === status.channelConnectionId ? { ...connection, webhookStatus: status } : connection
}

function mergeAuthorities(current: SourceAuthority[], updates: SourceAuthority[]): SourceAuthority[] {
  const byDomain = new Map(current.map(authority => [authority.domain, authority]))
  updates.forEach(authority => byDomain.set(authority.domain, authority))
  return Array.from(byDomain.values())
}

function isProductPullJobActive(job: ChannelProductPullJob): boolean {
  return job.status === 'QUEUED' || job.status === 'RUNNING' || job.status === 'DEFERRED'
}

function productPullJobTone(status: ChannelProductPullJobStatus): PillTone {
  if (status === 'COMPLETED') return 'success'
  if (status === 'COMPLETED_WITH_ISSUES' || status === 'DEFERRED') return 'warning'
  if (status === 'FAILED') return 'danger'
  return 'info'
}

function mergeLatestProductPullJobs(serverJobs: ChannelProductPullJob[], currentJobs: ChannelProductPullJob[]): ChannelProductPullJob[] {
  const currentById = new Map(currentJobs.map(job => [job.id, job]))
  const mergedServerJobs = serverJobs.map(job => {
    const current = currentById.get(job.id)
    return current && Date.parse(current.updatedAt) > Date.parse(job.updatedAt) ? current : job
  })
  const serverConnectionIds = new Set(mergedServerJobs.map(job => job.channelConnectionId))
  const optimisticActiveJobs = currentJobs.filter(job => isProductPullJobActive(job) && !serverConnectionIds.has(job.channelConnectionId))
  return [...mergedServerJobs, ...optimisticActiveJobs]
}

export function ChannelsPage({ accessToken, onOpenConnection, onReviewUnmapped, onOpenStockAllocation }: ChannelsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const sharedImports = useSharedImports()
  const [connections, setConnections] = useState<ChannelConnection[]>([])
  const [providers, setProviders] = useState<ChannelProviderOption[]>([])
  const [authorities, setAuthorities] = useState<SourceAuthority[]>([])
  const [onboardingJobs, setOnboardingJobs] = useState<ChannelOnboardingJob[]>([])
  const [orderPullJobs, setOrderPullJobs] = useState<ChannelOrderPullJob[]>([])
  const [productPullJobs, setProductPullJobs] = useState<ChannelProductPullJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [connectingId, setConnectingId] = useState<number | null>(null)
  const [tableError, setTableError] = useState<unknown>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [activeConnectionId, setActiveConnectionId] = useState<number | null>(null)
  const [sourceChoice, setSourceChoice] = useState<SourceChoice>('TLAAQ')
  const [pullChoice, setPullChoice] = useState<PullChoice>('BOOTSTRAP')
  const [savingSourceId, setSavingSourceId] = useState<number | null>(null)
  const [pullingId, setPullingId] = useState<number | null>(null)
  const [retryingJobId, setRetryingJobId] = useState<number | null>(null)
  const [retryingProductJobId, setRetryingProductJobId] = useState<number | null>(null)
  const [webhookStatuses, setWebhookStatuses] = useState<Record<number, ChannelWebhookStatus>>({})
  const [checkingWebhookId, setCheckingWebhookId] = useState<number | null>(null)
  const [registeringWebhookId, setRegisteringWebhookId] = useState<number | null>(null)
  const [credentialConnectionId, setCredentialConnectionId] = useState<number | null>(null)
  const [credentialForm, setCredentialForm] = useState<CredentialFormState>(EMPTY_CREDENTIAL_FORM)
  const [savingCredentialsId, setSavingCredentialsId] = useState<number | null>(null)
  const [credentialError, setCredentialError] = useState<unknown>(null)
  const [credentialSavedConnection, setCredentialSavedConnection] = useState<ChannelConnection | null>(null)
  const [orderPullingId, setOrderPullingId] = useState<number | null>(null)
  const [orderPullConnectionId, setOrderPullConnectionId] = useState<number | null>(null)
  const [orderPullForm, setOrderPullForm] = useState<OrderPullFormState>(EMPTY_ORDER_PULL_FORM)
  const [orderPullFormError, setOrderPullFormError] = useState<unknown>(null)
  const [selectedOrderPullJobConnectionId, setSelectedOrderPullJobConnectionId] = useState<number | null>(null)
  const [dismissedOrderPullJobIds, setDismissedOrderPullJobIds] = useState(loadDismissedOrderPullJobIds)
  const [dismissedProductPullJobIds, setDismissedProductPullJobIds] = useState(loadDismissedProductPullJobIds)
  const [copiedWebhookValue, setCopiedWebhookValue] = useState<string | null>(null)
  const [noonSalesImportCapability, setNoonSalesImportCapability] = useState<NoonSalesImportCapability | null>(null)
  const [noonSalesImportConnectionId, setNoonSalesImportConnectionId] = useState<number | null>(null)
  const [noonCatalogImportConnectionId, setNoonCatalogImportConnectionId] = useState<number | null>(null)
  const [latestAllocationRun, setLatestAllocationRun] = useState<StockAllocationRunSummary | null>(null)
  const noonWebhookStatusRefreshes = useRef<Set<number>>(new Set())
  const productPollRequestSequence = useRef(0)
  const productPollAppliedSequence = useRef(0)

  const applyConnectionList = (nextConnections: ChannelConnection[]) => {
    setConnections(nextConnections)
    setWebhookStatuses(current => {
      const next = webhookStatusesFromConnections(nextConnections)
      nextConnections.forEach(connection => {
        if (connection.provider !== 'NOON' || connection.status !== 'CONNECTED') return

        const incomingStatus = next[connection.id]
        const currentStatus = current[connection.id]
        if (hasNoonWebhookSetup(currentStatus) && !hasNoonWebhookSetup(incomingStatus)) {
          next[connection.id] = currentStatus
          return
        }
        if (!hasNoonWebhookSetup(incomingStatus)) {
          noonWebhookStatusRefreshes.current.delete(connection.id)
        }
      })
      return next
    })
  }

  const mergeOrderPullJob = (job: ChannelOrderPullJob) => {
    setOrderPullJobs(current => {
      const next = current.filter(existing => existing.id !== job.id && existing.channelConnectionId !== job.channelConnectionId)
      return [job, ...next]
    })
  }

  const mergeProductPullJob = (job: ChannelProductPullJob) => {
    setProductPullJobs(current => {
      const existing = current.find(item => item.id === job.id)
      const nextJob = existing && Date.parse(existing.updatedAt) > Date.parse(job.updatedAt) ? existing : job
      return [nextJob, ...current.filter(item => item.id !== job.id && item.channelConnectionId !== job.channelConnectionId)]
    })
  }

  useEffect(() => {
    let cancelled = false
    // Intentional reset on accessToken/reload changes so the list shows a loading state while
    // re-fetching. This is the canonical fetch-on-deps pattern used across data pages.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    // Product jobs are tenant-scoped; do not retain the prior token's results while reloading.
    setProductPullJobs([])
    productPollRequestSequence.current = 0
    productPollAppliedSequence.current = 0

    Promise.all([
      listChannelConnections(accessToken),
      listChannelProviders(accessToken),
      listSourceAuthorities(accessToken),
      listLatestChannelOnboardingJobs(accessToken),
      listLatestChannelOrderPullJobs(accessToken),
      listLatestChannelProductPullJobs(accessToken),
    ])
      .then(([nextConnections, nextProviders, nextAuthorities, nextJobs, nextOrderPullJobs, nextProductPullJobs]) => {
        if (cancelled) return
        applyConnectionList(nextConnections)
        setProviders(nextProviders)
        setAuthorities(nextAuthorities)
        setOnboardingJobs(nextJobs)
        setOrderPullJobs(nextOrderPullJobs)
        setProductPullJobs(nextProductPullJobs)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, reloadKey, sharedImports.revision])

  useEffect(() => {
    let cancelled = false
    void Promise.all(connections.map(connection => listStockAllocationRuns(accessToken, 1, connection.id)))
      .then(runsByConnection => {
        if (cancelled) return
        const latestFailure = runsByConnection
          .flatMap(runs => runs.slice(0, 1))
          .filter(run => run.failed > 0)
          .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0] ?? null
        setLatestAllocationRun(latestFailure)
      })
      .catch(() => { if (!cancelled) setLatestAllocationRun(null) })
    return () => { cancelled = true }
  }, [accessToken, connections, reloadKey, sharedImports.revision])

  useEffect(() => {
    if (!onboardingJobs.some(isOnboardingJobActive)) return

    const interval = window.setInterval(() => {
      void listLatestChannelOnboardingJobs(accessToken)
        .then(setOnboardingJobs)
        .catch(() => undefined)
    }, 2500)

    return () => window.clearInterval(interval)
  }, [accessToken, onboardingJobs])

  useEffect(() => {
    if (!orderPullJobs.some(isOrderPullJobActive)) return

    const interval = window.setInterval(() => {
      void listLatestChannelOrderPullJobs(accessToken)
        .then(nextJobs => setOrderPullJobs(current => mergeLatestOrderPullJobs(nextJobs, current)))
        .catch(() => undefined)
    }, 2500)

    return () => window.clearInterval(interval)
  }, [accessToken, orderPullJobs])

  useEffect(() => {
    if (!productPullJobs.some(isProductPullJobActive)) return

    let cancelled = false
    const interval = window.setInterval(() => {
      const sequence = ++productPollRequestSequence.current
      void listLatestChannelProductPullJobs(accessToken)
        .then(nextJobs => {
          if (cancelled || sequence < productPollAppliedSequence.current) return
          productPollAppliedSequence.current = sequence
          setProductPullJobs(current => mergeLatestProductPullJobs(nextJobs, current))
        })
        .catch(() => undefined)
    }, 2500)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [accessToken, productPullJobs])

  const rows = useMemo<ChannelConnection[]>(() => connections, [connections])
  const providerOptions = useMemo<ChannelProviderOption[]>(() => providers, [providers])
  const selectedProvider = providerOptions.find(option => option.provider === form.provider)
  const catalogAuthority = authorities.find(authority => authority.domain === 'PRODUCT_CATALOG')
  const stockAuthority = authorities.find(authority => authority.domain === 'STOCK')
  const activeConnection = rows.find(connection => connection.id === activeConnectionId) ?? null
  const credentialConnection = rows.find(connection => connection.id === credentialConnectionId) ?? null
  const orderPullConnection = rows.find(connection => connection.id === orderPullConnectionId) ?? null
  const noonSalesImportConnection = rows.find(connection => connection.id === noonSalesImportConnectionId) ?? null
  const noonCatalogImportConnection = rows.find(connection => connection.id === noonCatalogImportConnectionId && connection.status === 'CONNECTED') ?? null
  const latestJobByConnectionId = useMemo(() => new Map(onboardingJobs.map(job => [job.channelConnectionId, job])), [onboardingJobs])
  const latestOrderPullJobByConnectionId = useMemo(() => new Map(orderPullJobs.map(job => [job.channelConnectionId, job])), [orderPullJobs])
  const latestProductPullJobByConnectionId = useMemo(() => new Map(productPullJobs.map(job => [job.channelConnectionId, job])), [productPullJobs])
  const activeConnectionProductPullJob = activeConnection ? latestProductPullJobByConnectionId.get(activeConnection.id) : undefined
  const activeConnectionHasProductPullJob = activeConnectionProductPullJob != null && isProductPullJobActive(activeConnectionProductPullJob)
  const orderPullConnectionJob = orderPullConnection ? latestOrderPullJobByConnectionId.get(orderPullConnection.id) : undefined
  const orderPullConnectionHasActiveJob = orderPullConnectionJob != null && isOrderPullJobActive(orderPullConnectionJob)
  const visibleOrderPullJobs = useMemo(() => orderPullJobs
    .filter(job => isOrderPullJobActive(job) || !dismissedOrderPullJobIds.has(job.id))
    .map(job => ({ job, connection: rows.find(connection => connection.id === job.channelConnectionId) ?? null }))
    .filter((entry): entry is { job: ChannelOrderPullJob, connection: ChannelConnection } => entry.connection != null), [dismissedOrderPullJobIds, orderPullJobs, rows])
  const visibleProductPullJobs = useMemo(() => productPullJobs
    .filter(job => isProductPullJobActive(job) || !dismissedProductPullJobIds.has(job.id))
    .map(job => ({ job, connection: rows.find(connection => connection.id === job.channelConnectionId) ?? null }))
    .filter((entry): entry is { job: ChannelProductPullJob, connection: ChannelConnection } => entry.connection != null), [dismissedProductPullJobIds, productPullJobs, rows])
  const externalCatalogSource = catalogAuthority && catalogAuthority.ownerType !== 'TLAAQ' ? catalogAuthority : null
  const externalStockSource = stockAuthority && stockAuthority.ownerType !== 'TLAAQ' ? stockAuthority : null
  const hasInventorySource = isInventorySource(catalogAuthority) || isInventorySource(stockAuthority)
  const availableSourceChoices: SourceChoice[] = hasInventorySource ? ['KEEP_EXISTING', 'CHANNEL'] : ['TLAAQ', 'CHANNEL']
  const mobileActionStyle = mobileButtonStyle(isMobile)

  const dismissOrderPullJob = (jobId: number) => {
    setDismissedOrderPullJobIds(current => {
      const nextIds = [...current].filter(id => id !== jobId)
      nextIds.push(jobId)
      const retainedIds = nextIds.slice(-MAX_DISMISSED_ORDER_PULL_JOBS)
      try {
        window.localStorage.setItem(DISMISSED_ORDER_PULL_JOBS_STORAGE_KEY, JSON.stringify(retainedIds))
      } catch {
        // Keep the card dismissed for this session when browser storage is unavailable.
      }
      return new Set(retainedIds)
    })
  }

  const dismissProductPullJob = (jobId: number) => {
    setDismissedProductPullJobIds(current => {
      const nextIds = [...current].filter(id => id !== jobId)
      nextIds.push(jobId)
      const retainedIds = nextIds.slice(-MAX_DISMISSED_PRODUCT_PULL_JOBS)
      try {
        window.localStorage.setItem(DISMISSED_PRODUCT_PULL_JOBS_STORAGE_KEY, JSON.stringify(retainedIds))
      } catch {
        // Keep the card dismissed for this session when browser storage is unavailable.
      }
      return new Set(retainedIds)
    })
  }

  const applyWebhookStatus = useCallback((status: ChannelWebhookStatus) => {
    setWebhookStatuses(current => ({ ...current, [status.channelConnectionId]: status }))
    setConnections(current => current.map(item => mergeWebhookStatus(item, status)))
  }, [])

  useEffect(() => {
    noonWebhookStatusRefreshes.current.clear()
  }, [accessToken, reloadKey])

  useEffect(() => {
    let cancelled = false
    // Reset tenant-scoped capability and any open import panel before fetching for a new token/reload.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNoonSalesImportCapability(null)
    setNoonSalesImportConnectionId(null)
    setNoonCatalogImportConnectionId(null)
    void getNoonSalesImportCapability(accessToken)
      .then(capability => {
        if (!cancelled) setNoonSalesImportCapability(capability)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [accessToken, reloadKey])

  useEffect(() => {
    const candidates = rows.filter(connection => shouldFetchNoonWebhookStatus(connection, webhookStatuses[connection.id])
      && !noonWebhookStatusRefreshes.current.has(connection.id))
    if (candidates.length === 0) return

    candidates.forEach(connection => {
      noonWebhookStatusRefreshes.current.add(connection.id)
      void getChannelWebhookStatus(accessToken, connection.id)
        .then(applyWebhookStatus)
        .catch(() => {
          noonWebhookStatusRefreshes.current.delete(connection.id)
        })
    })
  }, [accessToken, applyWebhookStatus, rows, webhookStatuses])

  useEffect(() => {
    const nextAvailableSourceChoices: SourceChoice[] = hasInventorySource ? ['KEEP_EXISTING', 'CHANNEL'] : ['TLAAQ', 'CHANNEL']
    if (!activeConnection || nextAvailableSourceChoices.includes(sourceChoice)) return
    // Keep the open panel's selection valid when source authorities refresh in the background.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSourceChoice(nextAvailableSourceChoices[0])
  }, [activeConnection, hasInventorySource, sourceChoice])

  const refresh = () => setReloadKey(current => current + 1)

  const sourceLabel = (authority: SourceAuthority | null | undefined): string => {
    if (!authority || authority.ownerType === 'TLAAQ') return String(t('channels.source.owners.tlaaq'))
    if (authority.ownerType === 'CHANNEL_CONNECTION') {
      const connection = rows.find(row => row.id === authority.channelConnectionId)
      return connection?.displayName ?? String(t('channels.source.owners.channelFallback'))
    }
    return String(t('channels.source.owners.inventoryFallback', { provider: authority.provider ?? 'Zoho' }))
  }

  const isCatalogSource = (connection: ChannelConnection) =>
    catalogAuthority?.ownerType === 'CHANNEL_CONNECTION' && catalogAuthority.channelConnectionId === connection.id

  const isStockSource = (connection: ChannelConnection) =>
    stockAuthority?.ownerType === 'CHANNEL_CONNECTION' && stockAuthority.channelConnectionId === connection.id

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const provider = form.provider
    const displayName = form.displayName.trim()

    if (!provider || !displayName) {
      setFormError(new ApiClientError(400, { code: 'VALIDATION_FAILED' }))
      return
    }

    setSubmitting(true)
    setFormError(null)
    try {
      await createChannelConnection(accessToken, { provider, displayName })
      const [nextConnections, nextProviders, nextAuthorities] = await Promise.all([
        listChannelConnections(accessToken),
        listChannelProviders(accessToken),
        listSourceAuthorities(accessToken),
      ])
      applyConnectionList(nextConnections)
      setProviders(nextProviders)
      setAuthorities(nextAuthorities)
      setForm(EMPTY_FORM)
    } catch (caught) {
      setFormError(caught)
    } finally {
      setSubmitting(false)
    }
  }

  const startConnect = async (connection: ChannelConnection) => {
    setConnectingId(connection.id)
    setTableError(null)
    try {
      const result = await startChannelConnect(accessToken, connection.id)
      if (typeof window !== 'undefined' && result.authorizationUrl) {
        window.location.assign(result.authorizationUrl)
        return
      }
      setTableError(new ApiClientError(502, { code: 'CHANNEL_CONNECT_FAILED' }))
    } catch (caught) {
      setTableError(caught)
    } finally {
      setConnectingId(null)
    }
  }

  const canStartConnect = (connection: ChannelConnection): boolean => {
    if (!connection.adapterAvailable) return false
    if (connection.provider !== 'ZID' && connection.provider !== 'SALLA' && connection.provider !== 'AMAZON') return false
    if (connection.status === 'CONNECTED') return false
    return true
  }

  const openCredentialPanel = (connection: ChannelConnection) => {
    setNoonSalesImportConnectionId(null)
    setNoonCatalogImportConnectionId(null)
    setCredentialConnectionId(connection.id)
    setCredentialForm(EMPTY_CREDENTIAL_FORM)
    setCredentialError(null)
    setCredentialSavedConnection(null)
    setActiveConnectionId(null)
    setOrderPullConnectionId(null)
    setOrderPullForm(EMPTY_ORDER_PULL_FORM)
    setOrderPullFormError(null)
    setTableError(null)
  }

  const closeCredentialPanel = () => {
    setCredentialConnectionId(null)
    setCredentialForm(EMPTY_CREDENTIAL_FORM)
    setCredentialError(null)
    setCredentialSavedConnection(null)
  }

  const uploadNoonServiceAccountFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    if (!file) return

    try {
      const contents = await file.text()
      setCredentialForm(prev => ({ ...prev, serviceAccountJson: contents }))
      setCredentialError(null)
      setCredentialSavedConnection(null)
    } catch {
      setCredentialError(new ApiClientError(400, { code: 'CHANNEL_CREDENTIALS_JSON_INVALID' }))
    } finally {
      input.value = ''
    }
  }

  const closeOrderPullPanel = () => {
    setOrderPullConnectionId(null)
    setOrderPullForm(EMPTY_ORDER_PULL_FORM)
    setOrderPullFormError(null)
  }

  const submitCredentials = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!credentialConnection) return

    const sellerId = credentialForm.sellerId.trim()
    const apiKey = credentialForm.apiKey.trim()
    const apiSecret = credentialForm.apiSecret.trim()
    const userAgent = credentialForm.userAgent.trim()
    const refreshToken = credentialForm.refreshToken.trim()
    const marketplaceIds = credentialForm.marketplaceIds.trim()
    const region = credentialForm.region.trim()
    const serviceAccountJson = credentialForm.serviceAccountJson.trim()
    const warehouseCode = credentialForm.warehouseCode.trim()

    if (credentialConnection.provider === 'TRENDYOL' && (!sellerId || !apiKey || !apiSecret)) {
      setCredentialError(new ApiClientError(400, { code: 'CHANNEL_CREDENTIALS_REQUIRED' }))
      return
    }
    if (credentialConnection.provider === 'AMAZON' && (!sellerId || !refreshToken || !marketplaceIds)) {
      setCredentialError(new ApiClientError(400, { code: 'CHANNEL_CREDENTIALS_REQUIRED' }))
      return
    }
    if (credentialConnection.provider === 'NOON') {
      if (!warehouseCode) {
        setCredentialError(new ApiClientError(400, { code: 'CHANNEL_CREDENTIALS_WAREHOUSE_REQUIRED' }))
        return
      }
      const noonValidationCode = validateNoonServiceAccountJson(serviceAccountJson)
      if (noonValidationCode) {
        setCredentialError(new ApiClientError(400, { code: noonValidationCode }))
        return
      }
    }

    setSavingCredentialsId(credentialConnection.id)
    setCredentialError(null)
    setCredentialSavedConnection(null)
    try {
      const payload = credentialConnection.provider === 'AMAZON'
        ? {
            provider: 'AMAZON' as const,
            sellerId,
            refreshToken,
            marketplaceIds,
            region: region || null,
          }
        : credentialConnection.provider === 'NOON'
          ? {
              provider: 'NOON' as const,
              serviceAccountJson,
              warehouseCode,
            }
          : {
              provider: 'TRENDYOL' as const,
              sellerId,
              apiKey,
              apiSecret,
              userAgent: userAgent || null,
            }
      const updated = await storeChannelCredentials(accessToken, credentialConnection.id, payload)
      if (updated.provider === 'NOON') {
        noonWebhookStatusRefreshes.current.delete(updated.id)
      }
      setConnections(current => current.map(connection => connection.id === updated.id ? updated : connection))
      setWebhookStatuses(current => {
        const next = { ...current }
        if (updated.webhookStatus) {
          next[updated.id] = updated.webhookStatus
        } else {
          delete next[updated.id]
        }
        return next
      })
      setCredentialSavedConnection(updated)
      setCredentialForm(EMPTY_CREDENTIAL_FORM)
    } catch (caught) {
      setCredentialError(caught)
    } finally {
      setSavingCredentialsId(null)
    }
  }

  const submitOrderPull = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!orderPullConnection) return

    let params: ChannelPullOrdersParams
    try {
      params = buildOrderPullParams(orderPullForm)
    } catch (caught) {
      setOrderPullFormError(caught)
      return
    }

    setOrderPullingId(orderPullConnection.id)
    setOrderPullFormError(null)
    setTableError(null)
    setSelectedOrderPullJobConnectionId(null)
    try {
      const job = await enqueueChannelOrderPullJob(accessToken, orderPullConnection.id, params)
      mergeOrderPullJob(job)
      setSelectedOrderPullJobConnectionId(orderPullConnection.id)
      closeOrderPullPanel()
      void listChannelConnections(accessToken).then(applyConnectionList).catch(() => undefined)
    } catch (caught) {
      setTableError(caught)
      void listChannelConnections(accessToken).then(applyConnectionList).catch(() => undefined)
    } finally {
      setOrderPullingId(null)
    }
  }

  const retryOnboardingJob = async (job: ChannelOnboardingJob) => {
    setRetryingJobId(job.id)
    setTableError(null)
    try {
      const retried = await retryChannelOnboardingJob(accessToken, job.id)
      setOnboardingJobs(current => {
        const next = current.filter(existing => existing.id !== retried.id)
        return [retried, ...next]
      })
    } catch (caught) {
      setTableError(caught)
    } finally {
      setRetryingJobId(null)
    }
  }

  const checkWebhooks = async (connection: ChannelConnection) => {
    setCheckingWebhookId(connection.id)
    setTableError(null)
    try {
      const status = await getChannelWebhookStatus(accessToken, connection.id)
      applyWebhookStatus(status)
    } catch (caught) {
      setTableError(caught)
    } finally {
      setCheckingWebhookId(null)
    }
  }

  const registerWebhooks = async (connection: ChannelConnection) => {
    setRegisteringWebhookId(connection.id)
    setTableError(null)
    try {
      const status = await registerChannelWebhooks(accessToken, connection.id)
      applyWebhookStatus(status)
    } catch (caught) {
      setTableError(caught)
    } finally {
      setRegisteringWebhookId(null)
    }
  }

  const deleteChannel = async (connection: ChannelConnection) => {
    const confirmText = String(t('channels.confirmDelete', { name: connection.displayName }))
    if (typeof window !== 'undefined' && !window.confirm(confirmText)) return

    setDeletingId(connection.id)
    setTableError(null)
    try {
      await deleteChannelConnection(accessToken, connection.id, { confirmedProductsAndOrdersDeletion: true })
      const [nextConnections, nextAuthorities] = await Promise.all([
        listChannelConnections(accessToken),
        listSourceAuthorities(accessToken),
      ])
      applyConnectionList(nextConnections)
      setAuthorities(nextAuthorities)
    } catch (caught) {
      setTableError(caught)
    } finally {
      setDeletingId(null)
    }
  }

  const openSourcePanel = (connection: ChannelConnection) => {
    setNoonSalesImportConnectionId(null)
    setNoonCatalogImportConnectionId(null)
    setActiveConnectionId(connection.id)
    setCredentialConnectionId(null)
    setOrderPullConnectionId(null)
    setOrderPullForm(EMPTY_ORDER_PULL_FORM)
    setOrderPullFormError(null)
    setTableError(null)
    if (hasInventorySource) {
      setSourceChoice('KEEP_EXISTING')
      setPullChoice('LISTINGS_ONLY')
      return
    }
    if (isCatalogSource(connection) || isStockSource(connection)) {
      setSourceChoice('CHANNEL')
      setPullChoice('BOOTSTRAP')
      return
    }
    setSourceChoice('TLAAQ')
    setPullChoice('BOOTSTRAP')
  }

  const confirmSourceSwitch = (connection: ChannelConnection, nextChoice: MutableSourceChoice): boolean => {
    const nextOwnerType: SourceOwnerType = nextChoice === 'CHANNEL' ? 'CHANNEL_CONNECTION' : 'TLAAQ'
    const nextLabel = nextChoice === 'CHANNEL' ? connection.displayName : String(t('channels.source.owners.tlaaq'))
    const conflictingAuthority = (supportsStockSource(connection) ? [catalogAuthority, stockAuthority] : [catalogAuthority]).find(authority => {
      if (!authority || authority.implicitDefault) return false
      return !sameOwner(authority, nextOwnerType, nextChoice === 'CHANNEL' ? connection.id : undefined)
    })
    if (!conflictingAuthority || typeof window === 'undefined') return true
    return window.confirm(String(t('channels.source.confirmSwitch', {
      current: sourceLabel(conflictingAuthority),
      next: nextLabel,
    })))
  }

  const saveSourceChoice = async (connection: ChannelConnection, nextChoice: MutableSourceChoice) => {
    if (!confirmSourceSwitch(connection, nextChoice)) return null
    const payload = nextChoice === 'CHANNEL'
      ? { ownerType: 'CHANNEL_CONNECTION' as const, channelConnectionId: connection.id, replaceExisting: true }
      : { ownerType: 'TLAAQ' as const, replaceExisting: true }

    const updates = supportsStockSource(connection)
      ? await Promise.all([
          updateSourceAuthority(accessToken, 'PRODUCT_CATALOG', payload),
          updateSourceAuthority(accessToken, 'STOCK', payload),
        ])
      : [await updateSourceAuthority(accessToken, 'PRODUCT_CATALOG', payload)]
    setAuthorities(current => mergeAuthorities(current, updates))
    return updates
  }

  const handleSaveSourceOnly = async () => {
    if (!activeConnection) return
    setSavingSourceId(activeConnection.id)
    setTableError(null)
    try {
      if (sourceChoice === 'KEEP_EXISTING') return
      await saveSourceChoice(activeConnection, sourceChoice)
    } catch (caught) {
      setTableError(caught)
      void listSourceAuthorities(accessToken).then(setAuthorities).catch(() => undefined)
    } finally {
      setSavingSourceId(null)
    }
  }

  const handlePullProducts = async () => {
    if (!activeConnection || activeConnectionHasProductPullJob) return
    setPullingId(activeConnection.id)
    setTableError(null)
    try {
      if (sourceChoice !== 'KEEP_EXISTING') {
        const saved = await saveSourceChoice(activeConnection, sourceChoice)
        if (saved === null) return
      }
      const modes = sourceChoice === 'CHANNEL'
        ? { productMode: 'SOURCE_SYNC' as const, stockMode: supportsStockSource(activeConnection) ? 'IMPORT_SNAPSHOT' as const : 'NONE' as const }
        : sourceChoice === 'KEEP_EXISTING'
          ? { productMode: 'LISTINGS_ONLY' as const, stockMode: 'NONE' as const }
          : {
              productMode: pullChoice === 'BOOTSTRAP' ? 'IMPORT_MISSING' as const : 'LISTINGS_ONLY' as const,
              stockMode: 'NONE' as const,
            }
      const job = await enqueueChannelProductPullJob(accessToken, activeConnection.id, modes)
      mergeProductPullJob(job)
    } catch (caught) {
      setTableError(caught)
      void listSourceAuthorities(accessToken).then(setAuthorities).catch(() => undefined)
    } finally {
      setPullingId(null)
    }
  }

  const refreshAllNoonListings = async (connection: ChannelConnection) => {
    const existing = latestProductPullJobByConnectionId.get(connection.id)
    if (pullingId === connection.id || (existing && isProductPullJobActive(existing))) return
    setPullingId(connection.id)
    setTableError(null)
    try {
      const job = await enqueueChannelProductPullJob(accessToken, connection.id, {
        productMode: 'REFRESH_EXISTING', stockMode: 'NONE',
      })
      mergeProductPullJob(job)
      sharedImports.showProductJob(job)
    } catch (caught) {
      setTableError(caught)
    } finally {
      setPullingId(null)
    }
  }

  const retryProductPullJob = async (job: ChannelProductPullJob) => {
    setRetryingProductJobId(job.id)
    setTableError(null)
    try {
      mergeProductPullJob(await retryChannelProductPullJob(accessToken, job.id))
    } catch (caught) {
      setTableError(caught)
    } finally {
      setRetryingProductJobId(null)
    }
  }

  const formatWebhookCheckedAt = (checkedAt: string): string => {
    const date = new Date(checkedAt)
    if (Number.isNaN(date.getTime())) return checkedAt
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }

  const formatProductPullRetryAt = (retryAt: string): string => {
    const date = new Date(retryAt)
    if (Number.isNaN(date.getTime())) return retryAt
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }

  const formatOrderPullDate = (value: string): string => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(date)
  }

  const orderPullRangeLabel = (job: ChannelOrderPullJob): string => {
    if (job.dateFrom && job.dateTo) {
      return String(t('channels.ordersPull.rangeBetween', {
        from: formatOrderPullDate(job.dateFrom),
        to: formatOrderPullDate(job.dateTo),
      }))
    }
    if (job.dateFrom) return String(t('channels.ordersPull.rangeFrom', { from: formatOrderPullDate(job.dateFrom) }))
    if (job.dateTo) return String(t('channels.ordersPull.rangeTo', { to: formatOrderPullDate(job.dateTo) }))
    return String(t('channels.ordersPull.rangeDefault'))
  }

  const renderDesktopInfoBlock = (label: string, children: ReactNode) => (
    <div style={{ paddingBlock: 10, paddingInline: 11, borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--ink-50)', display: 'grid', gap: 6, ...TEXT_WRAP_STYLE }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>
        {label}
      </div>
      <div style={TEXT_WRAP_STYLE}>
        {children}
      </div>
    </div>
  )

  const renderConnectionSourceBadges = (connection: ChannelConnection) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
      {isCatalogSource(connection) && <Pill tone="brand" size="sm">{t('channels.source.catalogBadge')}</Pill>}
      {isStockSource(connection) && <Pill tone="info" size="sm">{t('channels.source.stockBadge')}</Pill>}
      {!isCatalogSource(connection) && !isStockSource(connection) && (
        <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('channels.source.notSource')}</span>
      )}
    </div>
  )

  const copyWebhookValue = async (value: string) => {
    const clipboard = (typeof window !== 'undefined' ? window.navigator.clipboard : undefined)
      ?? (typeof navigator !== 'undefined' ? navigator.clipboard : undefined)
    if (!clipboard) return
    await clipboard.writeText(value)
    setCopiedWebhookValue(value)
  }

  const renderNoonWebhookSetup = (status: ChannelWebhookStatus | undefined, compactButtons: boolean) => {
    const setup = noonWebhookSetup(status)
    if (!setup.targetUrl && !setup.apiKey) {
      return <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('channels.webhooks.noonPending')}</span>
    }
    const compactButtonStyle = compactButtons ? COMPACT_FULL_WIDTH_BUTTON_STYLE : undefined
    return (
      <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.35, ...TEXT_WRAP_STYLE }}>
          {t('channels.webhooks.noonFbpiHelper')}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', lineHeight: 1.35, ...TEXT_WRAP_STYLE }}>
          {t('channels.webhooks.noonManualOwnership')}
        </div>
        {setup.targetUrl && (
          <div style={{ display: 'grid', gap: 5 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--fg-subtle)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('channels.webhooks.noonTargetUrl')}</div>
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{setup.targetUrl}</code>
            <Button kind="ghost" size="xs" type="button" onClick={() => void copyWebhookValue(setup.targetUrl!)} style={compactButtonStyle}>
              {copiedWebhookValue === setup.targetUrl ? t('channels.webhooks.copied') : t('channels.webhooks.copyUrl')}
            </Button>
          </div>
        )}
        {setup.apiKey && (
          <div style={{ display: 'grid', gap: 5 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--fg-subtle)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('channels.webhooks.noonApiKey')}</div>
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{setup.apiKey}</code>
            <Button kind="ghost" size="xs" type="button" onClick={() => void copyWebhookValue(setup.apiKey!)} style={compactButtonStyle}>
              {copiedWebhookValue === setup.apiKey ? t('channels.webhooks.copied') : t('channels.webhooks.copyApiKey')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderConnectionOnboarding = (connection: ChannelConnection, compactButtons = false) => {
    const compactButtonStyle = compactButtons ? COMPACT_FULL_WIDTH_BUTTON_STYLE : undefined
    const job = latestJobByConnectionId.get(connection.id)
    if (!job) {
      return (connection.provider === 'ZID' || connection.provider === 'SALLA') && connection.status === 'CONNECTED'
        ? <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('channels.onboarding.none')}</span>
        : <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('channels.onboarding.notApplicable')}</span>
    }
    return (
      <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Pill tone={onboardingJobTone(job.status)} size="sm" dot>
            {t(`channels.onboarding.status.${job.status}`)}
          </Pill>
          {job.status === 'FAILED' && (
            <Button kind="ghost" size="xs" icon={I.refresh} onClick={() => void retryOnboardingJob(job)} disabled={retryingJobId === job.id} style={compactButtonStyle}>
              {retryingJobId === job.id ? t('channels.onboarding.retrying') : t('channels.onboarding.retry')}
            </Button>
          )}
        </div>
        <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, lineHeight: 1.35, ...TEXT_WRAP_STYLE }}>
          {t('channels.onboarding.progress', {
            products: job.productUpserted,
            orders: job.orderUpserted,
          })}
        </div>
        {job.productIssueCount > 0 && (
          <div style={{ color: 'var(--warning)', fontSize: 11.5, fontWeight: 700 }}>
            {t('channels.onboarding.issues', { count: job.productIssueCount })}
          </div>
        )}
      </div>
    )
  }

  const renderConnectionWebhooks = (connection: ChannelConnection, compactButtons = false) => {
    const compactButtonStyle = compactButtons ? COMPACT_FULL_WIDTH_BUTTON_STYLE : undefined
    const status = webhookStatuses[connection.id]
    if (connection.provider === 'NOON') {
      return renderNoonWebhookSetup(status, compactButtons)
    }
    if (!supportsWebhookManagement(connection)) {
      return <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{t('channels.webhooks.notApplicable')}</span>
    }
    return (
      <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Pill tone={status ? (status.registered ? 'success' : 'warning') : 'neutral'} size="sm" dot={Boolean(status)}>
            {status
              ? t(status.registered ? 'channels.webhooks.registered' : 'channels.webhooks.missing', {
                  registered: status.registeredCount,
                  expected: status.expectedCount,
                })
              : t('channels.webhooks.unchecked')}
          </Pill>
        </div>
        {status && status.missingCount > 0 && (
          <div style={{ color: 'var(--warning)', fontSize: 11.5, fontWeight: 700 }}>
            {t('channels.webhooks.missingCount', { count: status.missingCount })}
          </div>
        )}
        {status && (
          <div style={{ color: 'var(--fg-subtle)', fontSize: 11.5, ...TEXT_WRAP_STYLE }}>
            {t('channels.webhooks.lastChecked', { checkedAt: formatWebhookCheckedAt(status.checkedAt) })}
          </div>
        )}
        <div style={{ display: compactButtons ? 'grid' : 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Button kind="ghost" size="xs" icon={I.eye} onClick={() => void checkWebhooks(connection)} disabled={checkingWebhookId === connection.id || registeringWebhookId === connection.id} style={compactButtonStyle}>
            {checkingWebhookId === connection.id ? t('channels.webhooks.checking') : t('channels.webhooks.check')}
          </Button>
          <Button kind="ghost" size="xs" icon={I.refresh} onClick={() => void registerWebhooks(connection)} disabled={registeringWebhookId === connection.id || checkingWebhookId === connection.id} style={compactButtonStyle}>
            {registeringWebhookId === connection.id ? t('channels.webhooks.registering') : t('channels.webhooks.reregister')}
          </Button>
        </div>
      </div>
    )
  }

  const renderConnectionActions = (connection: ChannelConnection, compact = false) => {
    const compactActionStyle = compact ? COMPACT_FULL_WIDTH_BUTTON_STYLE : undefined
    const orderPullJob = latestOrderPullJobByConnectionId.get(connection.id)
    const orderPullActive = orderPullJob != null && isOrderPullJobActive(orderPullJob)
    const orderPullBusy = orderPullingId === connection.id || orderPullActive
    const productJob = latestProductPullJobByConnectionId.get(connection.id)
    const refreshBusy = pullingId === connection.id || (productJob != null && isProductPullJobActive(productJob))
    return (
      <div style={{ display: compact ? 'grid' : 'flex', gap: 8, justifyContent: compact ? undefined : 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button kind="ghost" size="xs" icon={I.clock} onClick={() => sharedImports.openForChannel(connection.id)} style={compactActionStyle}>
          {t('imports.title')}
        </Button>
        {connection.status !== 'DISCONNECTED' && connection.provider === 'NOON' && (
          <Button kind="secondary" size="xs" icon={I.download} style={compactActionStyle} onClick={() => sharedImports.openSetup('noon-catalog', connection.id)}>
            {t('channels.noonCatalogImport.action')}
          </Button>
        )}
        {connection.status === 'CONNECTED' && connection.provider === 'NOON' && connection.adapterAvailable && (
          <Button kind="secondary" size="xs" icon={I.refresh} style={compactActionStyle} disabled={refreshBusy}
            onClick={() => void refreshAllNoonListings(connection)}>
            {t(refreshBusy ? 'channels.noonRefresh.active' : 'channels.noonRefresh.action')}
          </Button>
        )}
        {connection.status === 'CONNECTED' && connection.provider !== 'NOON' && (
          <Button
            kind="secondary"
            size="xs"
            icon={I.download}
            onClick={() => sharedImports.openSetup('products', connection.id)}
            disabled={connection.capabilities?.catalogPull.enabled === false || pullingId === connection.id || savingSourceId === connection.id || (latestProductPullJobByConnectionId.get(connection.id) != null && isProductPullJobActive(latestProductPullJobByConnectionId.get(connection.id) as ChannelProductPullJob))}
            style={compactActionStyle}
          >
            {latestProductPullJobByConnectionId.get(connection.id) && isProductPullJobActive(latestProductPullJobByConnectionId.get(connection.id) as ChannelProductPullJob)
              ? t('channels.actions.productPullActive')
              : t('channels.actions.pullProducts')}
          </Button>
        )}
        {connection.status === 'CONNECTED' && connection.provider !== 'NOON' && (
          <Button kind="ghost" size="xs" icon={I.settings} onClick={() => openSourcePanel(connection)} style={compactActionStyle}>
            {t('channels.actions.manageSource')}
          </Button>
        )}
        {connection.status === 'CONNECTED' && onReviewUnmapped && (
          <Button
            kind="ghost"
            size="xs"
            icon={I.link}
            onClick={() => onReviewUnmapped(connection.id)}
            style={compactActionStyle}
          >
            {t('channels.actions.reviewUnmappedShort')}
          </Button>
        )}
        {connection.provider === 'NOON' && connection.status !== 'DISCONNECTED' && noonSalesImportCapability?.enabled && (
          <Button
            kind="secondary"
            size="xs"
            icon={I.download}
            onClick={() => sharedImports.openSetup('noon-sales', connection.id)}
            style={compactActionStyle}
          >
            {t('channels.noonSalesImport.action')}
          </Button>
        )}
        {supportsManualOrderPull(connection) && connection.status === 'CONNECTED' && (
          <Button
            kind="secondary"
            size="xs"
            icon={I.refresh}
            onClick={() => sharedImports.openSetup('orders', connection.id)}
            disabled={orderPullBusy}
            style={compactActionStyle}
          >
            {orderPullBusy ? t('channels.actions.pullingOrders') : t('channels.actions.pullOrders')}
          </Button>
        )}
        {supportsManualCredentials(connection) && (
          <Button
          kind={connection.status === 'CONNECTED' ? 'ghost' : 'primary'}
          size="xs"
          onClick={() => openCredentialPanel(connection)}
          disabled={savingCredentialsId === connection.id}
          style={compactActionStyle}
        >
          {connection.status === 'CONNECTED'
            ? t('channels.actions.updateCredentials')
            : connection.status === 'AUTH_EXPIRED'
              ? t('channels.actions.reconnectCredentials')
              : t('channels.actions.enterCredentials')}
          </Button>
        )}
        {canStartConnect(connection) && (
        <Button
          kind="primary"
          size="xs"
          onClick={() => startConnect(connection)}
          disabled={connectingId === connection.id}
          style={compactActionStyle}
        >
          {connectingId === connection.id ? t('channels.actions.connecting') : t('channels.actions.connect')}
        </Button>
        )}
        {onOpenConnection && (
          <Button
            kind="ghost"
            size="xs"
            icon={I.settings}
            onClick={() => onOpenConnection(connection.id)}
            style={compactActionStyle}
          >
            {t('channels.actions.manage')}
          </Button>
        )}
        <Button
          kind="danger"
          size="xs"
          onClick={() => deleteChannel(connection)}
          disabled={deletingId === connection.id}
          style={compactActionStyle}
        >
          {deletingId === connection.id ? t('channels.actions.deleting') : t('channels.actions.delete')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: 20, gap: 16 }}>
        <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: 0, maxWidth: 680, ...TEXT_WRAP_STYLE }}>
          {t('channels.description')}
        </p>
        <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, alignItems: 'center' }}>
          {onOpenStockAllocation && (
            <Button kind="primary" size="sm" icon={I.layers} onClick={() => onOpenStockAllocation('rules')} style={mobileActionStyle}>
              {t('channels.actions.stockAllocation')}
            </Button>
          )}
          <Button kind="secondary" size="sm" icon={I.refresh} onClick={refresh} disabled={loading} style={mobileActionStyle}>
            {t('channels.actions.refresh')}
          </Button>
        </div>
      </div>

      {latestAllocationRun && latestAllocationRun.failed > 0 && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: 'var(--warning-100)', background: 'var(--warning-50)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : '20px minmax(0, 1fr) auto', gap: 12, alignItems: 'center' }}>
            <Icon size={18} color="var(--warning)">{I.alert}</Icon>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('channels.stockSyncAlert.title', { provider: latestAllocationRun.provider })}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 3, ...TEXT_WRAP_STYLE }}>{t('channels.stockSyncAlert.body', { failed: latestAllocationRun.failed, pushed: latestAllocationRun.pushed })}</div>
            </div>
            {onOpenStockAllocation && <Button kind="secondary" size="sm" onClick={() => onOpenStockAllocation('activity')} style={mobileActionStyle}>{t('channels.stockSyncAlert.action')}</Button>}
          </div>
        </Card>
      )}

      {(externalCatalogSource || externalStockSource) && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: 'var(--brand-300)', background: 'var(--brand-100)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 10, alignItems: 'start' }}>
            <Icon size={18} color="var(--brand-700)">{I.shield}</Icon>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>
                {t('channels.source.bannerTitle')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.55 }}>
                {t('channels.source.bannerBody', {
                  catalog: sourceLabel(catalogAuthority),
                  stock: sourceLabel(stockAuthority),
                })}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card padding={20} style={{ marginBottom: 16 }}>
        <form onSubmit={submitForm}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 12 }}>
            {t('channels.form.title')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(180px, 0.8fr) minmax(240px, 1.2fr) auto', gap: 12, alignItems: 'end' }}>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.form.provider')}</div>
              <select
                value={form.provider}
                onChange={event => setForm(prev => ({ ...prev, provider: event.target.value as ChannelProvider | '' }))}
                style={{
                  width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                  color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                  borderRadius: 9, outline: 'none',
                }}
              >
                <option value="">{t('channels.form.providerPlaceholder')}</option>
                {providerOptions.map(option => (
                  <option key={option.provider} value={option.provider}>
                    {t(`channels.providers.${option.provider}`)} · {option.adapterAvailable ? t('channels.adapter.available') : t('channels.adapter.comingLater')}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.form.displayName')}</div>
              <input
                value={form.displayName}
                onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
                placeholder={t('channels.form.displayNamePlaceholder')}
                style={{
                  width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                  color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                  borderRadius: 9, outline: 'none',
                }}
              />
            </label>
            <Button kind="primary" size="sm" type="submit" icon={I.plus} disabled={submitting || loading} style={{ justifyContent: 'center', ...mobileActionStyle }}>
              {submitting ? t('channels.form.submitting') : t('channels.form.submit')}
            </Button>
          </div>
          <div style={{ marginTop: 10, display: 'grid', gap: 6, fontSize: 12.5, color: 'var(--fg-muted)' }}>
            <div>{t('channels.form.helper')}</div>
            {selectedProvider && !selectedProvider.adapterAvailable && (
              <div style={{ color: 'var(--warning)', fontWeight: 700 }}>{t('channels.adapter.comingLater')}</div>
            )}
          </div>
          {formError !== null && (
            <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
              {String(t(errorMessageKey(formError), { defaultValue: String(t('channels.errors.generic')) }))}
            </div>
          )}
        </form>
      </Card>

      {credentialConnection && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--brand-300)' }}>
          <form onSubmit={submitCredentials}>
            <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <ChannelDot name={PROVIDER_DOT_NAMES[credentialConnection.provider]} size={30} />
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)' }}>
                    {t(`channels.credentials.${credentialConnection.provider === 'AMAZON' ? 'amazonTitle' : credentialConnection.provider === 'NOON' ? 'noonTitle' : 'trendyolTitle'}`, { name: credentialConnection.displayName })}
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55, maxWidth: 680 }}>
                  {t(`channels.credentials.${credentialConnection.provider === 'AMAZON' ? 'amazonSubtitle' : credentialConnection.provider === 'NOON' ? 'noonSubtitle' : 'trendyolSubtitle'}`)}
                </p>
              </div>
              <Button kind="ghost" size="xs" type="button" onClick={closeCredentialPanel} style={mobileActionStyle}>
                {t('channels.credentials.cancel')}
              </Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 14 }}>
              {credentialConnection.provider === 'NOON' ? (
                <div style={{ display: 'grid', gap: 10, gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.noonWarehouseCode')}</div>
                    <input
                      value={credentialForm.warehouseCode}
                      onChange={event => setCredentialForm(prev => ({ ...prev, warehouseCode: event.target.value }))}
                      placeholder={t('channels.credentials.noonWarehouseCodePlaceholder')}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.noonServiceAccountJson')}</div>
                    <textarea
                      value={credentialForm.serviceAccountJson}
                      onChange={event => setCredentialForm(prev => ({ ...prev, serviceAccountJson: event.target.value }))}
                      placeholder={t('channels.credentials.noonServiceAccountJsonPlaceholder')}
                      rows={10}
                      spellCheck={false}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 12.5, fontFamily: 'var(--font-mono)', lineHeight: 1.5,
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none', resize: 'vertical', minHeight: 180,
                      }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.noonServiceAccountUpload')}</div>
                    <input
                      type="file"
                      accept="application/json,.json"
                      onChange={event => void uploadNoonServiceAccountFile(event)}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)', lineHeight: 1.45 }}>
                    {t('channels.credentials.noonServiceAccountRequiredFields')}
                  </div>
                </div>
              ) : (
                <label style={{ display: 'block' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.sellerId')}</div>
                  <input
                    value={credentialForm.sellerId}
                    onChange={event => setCredentialForm(prev => ({ ...prev, sellerId: event.target.value }))}
                    placeholder={t(`channels.credentials.${credentialConnection.provider === 'AMAZON' ? 'amazonSellerIdPlaceholder' : 'trendyolSellerIdPlaceholder'}`)}
                    style={{
                      width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                      color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                      borderRadius: 9, outline: 'none',
                    }}
                  />
                </label>
              )}
              {credentialConnection.provider === 'AMAZON' ? (
                <>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.amazonMarketplaceIds')}</div>
                    <input
                      value={credentialForm.marketplaceIds}
                      onChange={event => setCredentialForm(prev => ({ ...prev, marketplaceIds: event.target.value }))}
                      placeholder={t('channels.credentials.amazonMarketplaceIdsPlaceholder')}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.amazonRegion')}</div>
                    <select
                      value={credentialForm.region}
                      onChange={event => setCredentialForm(prev => ({ ...prev, region: event.target.value }))}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    >
                      <option value="EU">{t('channels.credentials.amazonRegions.EU')}</option>
                      <option value="NA">{t('channels.credentials.amazonRegions.NA')}</option>
                      <option value="FE">{t('channels.credentials.amazonRegions.FE')}</option>
                    </select>
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.amazonRefreshToken')}</div>
                    <input
                      type="password"
                      value={credentialForm.refreshToken}
                      onChange={event => setCredentialForm(prev => ({ ...prev, refreshToken: event.target.value }))}
                      autoComplete="new-password"
                      placeholder={t('channels.credentials.amazonRefreshTokenPlaceholder')}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                </>
              ) : credentialConnection.provider === 'NOON' ? null : (
                <>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.userAgent')}</div>
                    <input
                      value={credentialForm.userAgent}
                      onChange={event => setCredentialForm(prev => ({ ...prev, userAgent: event.target.value }))}
                      placeholder={t('channels.credentials.userAgentPlaceholder')}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.apiKey')}</div>
                    <input
                      type="password"
                      value={credentialForm.apiKey}
                      onChange={event => setCredentialForm(prev => ({ ...prev, apiKey: event.target.value }))}
                      autoComplete="new-password"
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('channels.credentials.apiSecret')}</div>
                    <input
                      type="password"
                      value={credentialForm.apiSecret}
                      onChange={event => setCredentialForm(prev => ({ ...prev, apiSecret: event.target.value }))}
                      autoComplete="new-password"
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none',
                      }}
                    />
                  </label>
                </>
              )}
            </div>

            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.5 }}>
              {t(`channels.credentials.${credentialConnection.provider === 'AMAZON' ? 'amazonHelper' : credentialConnection.provider === 'NOON' ? 'noonHelper' : 'trendyolHelper'}`)}
            </div>
            {credentialError !== null && (
              <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
                {String(t(errorMessageKey(credentialError), { defaultValue: String(t('channels.errors.generic')) }))}
              </div>
            )}
            {credentialSavedConnection && (
              <div style={{ marginTop: 12, color: 'var(--success)', fontSize: 12.5, fontWeight: 800 }}>
                {t('channels.credentials.saved', { name: credentialSavedConnection.displayName })}
              </div>
            )}
            <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <Button kind="secondary" size="sm" type="button" onClick={closeCredentialPanel} style={mobileActionStyle}>
                {t('channels.credentials.cancel')}
              </Button>
              <Button kind="primary" size="sm" type="submit" disabled={savingCredentialsId === credentialConnection.id} style={mobileActionStyle}>
                {savingCredentialsId === credentialConnection.id ? t('channels.credentials.saving') : t('channels.credentials.save')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {orderPullConnection && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--brand-300)' }}>
          <form onSubmit={submitOrderPull}>
            <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <ChannelDot name={PROVIDER_DOT_NAMES[orderPullConnection.provider]} size={30} />
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>
                    {t('channels.ordersPull.panelTitle', { name: orderPullConnection.displayName })}
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55, maxWidth: 680, ...TEXT_WRAP_STYLE }}>
                  {t('channels.ordersPull.panelSubtitle')}
                </p>
              </div>
              <Button kind="ghost" size="xs" type="button" onClick={closeOrderPullPanel} style={mobileActionStyle}>
                {t('channels.ordersPull.cancel')}
              </Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 14 }}>
              <Field
                type="date"
                label={String(t('channels.ordersPull.dateFrom'))}
                hint={String(t('channels.ordersPull.dateFromHint'))}
                value={orderPullForm.dateFrom}
                onChange={event => setOrderPullForm(prev => ({ ...prev, dateFrom: event.target.value }))}
              />
              <Field
                type="date"
                label={String(t('channels.ordersPull.dateTo'))}
                hint={String(t('channels.ordersPull.dateToHint'))}
                value={orderPullForm.dateTo}
                onChange={event => setOrderPullForm(prev => ({ ...prev, dateTo: event.target.value }))}
              />
            </div>

            {orderPullConnectionHasActiveJob && (
              <div style={{ marginTop: 12, color: 'var(--warning)', fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}>
                {t('channels.ordersPull.activeForConnection')}
              </div>
            )}
            {orderPullFormError !== null && (
              <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
                {String(t(errorMessageKey(orderPullFormError), { defaultValue: String(t('channels.errors.generic')) }))}
              </div>
            )}
            <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <Button kind="secondary" size="sm" type="button" onClick={closeOrderPullPanel} style={mobileActionStyle}>
                {t('channels.ordersPull.cancel')}
              </Button>
              <Button kind="primary" size="sm" type="submit" icon={I.refresh} disabled={orderPullingId === orderPullConnection.id || orderPullConnectionHasActiveJob} style={mobileActionStyle}>
                {orderPullingId === orderPullConnection.id ? t('channels.ordersPull.queueing') : t('channels.ordersPull.queue')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {noonCatalogImportConnection && (
        <NoonCatalogImportPanel
          key={`${accessToken}-${noonCatalogImportConnection.id}`}
          accessToken={accessToken}
          connection={noonCatalogImportConnection}
          onClose={() => setNoonCatalogImportConnectionId(null)}
          onReviewUnmapped={onReviewUnmapped}
        />
      )}

      {noonSalesImportConnection && noonSalesImportCapability?.enabled && (
        <NoonSalesImportPanel
          key={`${accessToken}-${noonSalesImportConnection.id}`}
          accessToken={accessToken}
          connection={noonSalesImportConnection}
          capability={noonSalesImportCapability}
          onClose={() => setNoonSalesImportConnectionId(null)}
        />
      )}

      {activeConnection && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--brand-300)' }}>
          <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <ChannelDot name={PROVIDER_DOT_NAMES[activeConnection.provider]} size={30} />
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)' }}>
                  {t('channels.source.panelTitle', { name: activeConnection.displayName })}
                </div>
              </div>
              <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55, maxWidth: 680 }}>
                {t('channels.source.panelSubtitle')}
              </p>
              {activeConnection.provider === 'AMAZON' && (
                <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: '8px 0 0', lineHeight: 1.5, maxWidth: 680, fontWeight: 700 }}>
                  {t('channels.source.amazonCatalogOnly')}
                </p>
              )}
            </div>
            <Button kind="ghost" size="xs" onClick={() => setActiveConnectionId(null)} style={mobileActionStyle}>
              {t('channels.source.close')}
            </Button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) minmax(0, 1fr)'), gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                {t('channels.source.choiceLabel')}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {availableSourceChoices.map(choice => {
                  const selected = sourceChoice === choice
                  const titleKey = choice === 'CHANNEL'
                    ? 'channels.source.choices.channel.title'
                    : choice === 'KEEP_EXISTING'
                      ? 'channels.source.choices.keepExisting.title'
                      : 'channels.source.choices.tlaaq.title'
                  const bodyKey = choice === 'CHANNEL'
                    ? 'channels.source.choices.channel.body'
                    : choice === 'KEEP_EXISTING'
                      ? 'channels.source.choices.keepExisting.body'
                      : 'channels.source.choices.tlaaq.body'
                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={() => setSourceChoice(choice)}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 22px', gap: 10, textAlign: 'start',
                        padding: '13px 14px', borderRadius: 12, border: selected ? '1.5px solid var(--brand-500)' : '1px solid var(--border-subtle)',
                        background: selected ? 'var(--brand-100)' : 'white', cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <span>
                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>
                          {t(titleKey, { name: activeConnection.displayName })}
                        </span>
                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}>
                          {t(bodyKey, {
                            catalog: sourceLabel(catalogAuthority),
                            stock: sourceLabel(stockAuthority),
                          })}
                        </span>
                      </span>
                      <span style={{ width: 20, height: 20, borderRadius: '50%', border: selected ? '6px solid var(--brand-500)' : '2px solid var(--border-default)', background: 'white' }} />
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                {t('channels.source.import.title')}
              </div>
              {sourceChoice === 'CHANNEL' ? (
                <div style={{ padding: '13px 14px', borderRadius: 12, border: '1px solid var(--brand-300)', background: 'var(--brand-100)' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>
                    {t('channels.source.import.sourceSyncTitle')}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>
                    {t('channels.source.import.sourceSyncBody')}
                  </div>
                </div>
              ) : sourceChoice === 'KEEP_EXISTING' ? (
                <div style={{ padding: '13px 14px', borderRadius: 12, border: '1px solid var(--brand-300)', background: 'var(--brand-100)' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>
                    {t('channels.source.import.keepExistingTitle')}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>
                    {t('channels.source.import.keepExistingBody')}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {(['BOOTSTRAP', 'LISTINGS_ONLY'] as PullChoice[]).map(choice => {
                    const selected = pullChoice === choice
                    const titleKey = choice === 'BOOTSTRAP' ? 'channels.source.import.bootstrapTitle' : 'channels.source.import.listingsOnlyTitle'
                    const bodyKey = choice === 'BOOTSTRAP' ? 'channels.source.import.bootstrapBody' : 'channels.source.import.listingsOnlyBody'
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={() => setPullChoice(choice)}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr 22px', gap: 10, textAlign: 'start',
                          padding: '13px 14px', borderRadius: 12, border: selected ? '1.5px solid var(--brand-500)' : '1px solid var(--border-subtle)',
                          background: selected ? 'var(--brand-100)' : 'white', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        <span>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t(titleKey)}</span>
                          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}>{t(bodyKey)}</span>
                        </span>
                        <span style={{ width: 20, height: 20, borderRadius: '50%', border: selected ? '6px solid var(--brand-500)' : '2px solid var(--border-default)', background: 'white' }} />
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            {sourceChoice !== 'KEEP_EXISTING' && (
              <Button kind="secondary" size="sm" onClick={handleSaveSourceOnly} disabled={savingSourceId === activeConnection.id || pullingId === activeConnection.id} style={mobileActionStyle}>
                {savingSourceId === activeConnection.id ? t('channels.actions.savingSource') : t('channels.actions.saveSource')}
              </Button>
            )}
            <Button kind="primary" size="sm" icon={I.download} onClick={handlePullProducts} disabled={pullingId === activeConnection.id || savingSourceId === activeConnection.id || activeConnectionHasProductPullJob} style={mobileActionStyle}>
              {pullingId === activeConnection.id
                ? t('channels.actions.queueingProducts')
                : activeConnectionHasProductPullJob
                  ? t('channels.actions.productPullActive')
                : sourceChoice === 'KEEP_EXISTING'
                  ? t('channels.actions.refreshListings')
                  : t('channels.actions.pullAndImport')}
            </Button>
          </div>
        </Card>
      )}

      {visibleProductPullJobs.length > 0 && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: visibleProductPullJobs.some(({ job }) => isProductPullJobActive(job)) ? 'var(--brand-200)' : 'var(--border-subtle)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 12 }}>
            {t('channels.productPull.jobsTitle')}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {visibleProductPullJobs.map(({ job, connection }) => {
              const active = isProductPullJobActive(job)
              const retainedProgress = job.pagesCompleted > 0 && (job.status === 'DEFERRED' || job.status === 'FAILED')
              const noonRefresh = job.productMode === 'REFRESH_EXISTING'
              return (
                <div key={job.id} style={{ padding: 12, borderRadius: 12, background: 'var(--ink-50)', border: `1px solid ${active ? 'var(--brand-200)' : job.status === 'FAILED' ? 'var(--danger-100)' : 'var(--border-subtle)'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>
                      {t(noonRefresh ? 'channels.noonRefresh.title' : `channels.productPull.title.${job.status}`, { name: connection.displayName })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Pill tone={productPullJobTone(job.status)} size="sm" dot>
                        {t(`channels.productPull.status.${job.status}`)}
                      </Pill>
                      {!active && (
                        <Button
                          kind="ghost"
                          size="xs"
                          icon={I.x}
                          onClick={() => dismissProductPullJob(job.id)}
                          ariaLabel={t('channels.productPull.dismiss', { name: connection.displayName })}
                          style={{ padding: 6, color: 'var(--fg-muted)' }}
                        />
                      )}
                    </div>
                  </div>
                  {noonRefresh && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 10 }}>
                      {t('channels.noonRefresh.description')}
                    </div>
                  )}
                  {job.status === 'DEFERRED' && job.retryAt && (
                    <div style={{ fontSize: 12.5, color: 'var(--warning)', marginBottom: 10 }}>
                      {t('channels.productPull.deferredUntil', { time: formatProductPullRetryAt(job.retryAt) })}
                    </div>
                  )}
                  {retainedProgress && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 10 }}>
                      {t(noonRefresh ? 'channels.noonRefresh.retainedProgress' : 'channels.productPull.retainedProgress', { count: noonRefresh ? job.fetched : job.pagesCompleted })}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(130px, 1fr))' : `repeat(${noonRefresh ? 3 : 4}, minmax(0, 1fr))`, gap: 10 }}>
                    {(noonRefresh ? [
                      ['processed', job.fetched],
                      ['refreshed', job.upserted],
                      ['issues', job.issueCount],
                    ] as const : [
                      ['productsCreated', job.productsCreated],
                      ['mapped', job.mapped],
                      ['productsSkipped', job.productsSkipped],
                      ['stockRowsImported', job.stockRowsImported],
                    ] as const).map(([key, value]) => (
                      <div key={key} style={{ padding: 12, borderRadius: 12, background: 'white', border: '1px solid var(--border-subtle)' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)' }}>{value}</div>
                        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{t(`channels.${noonRefresh ? 'noonRefresh' : 'source'}.result.${key}`)}</div>
                      </div>
                    ))}
                  </div>
                  {!noonRefresh && <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 10 }}>
                    {t('channels.source.result.fetched', { count: job.fetched })}
                    {' · '}
                    {t('channels.source.result.listings', { count: job.upserted })}
                  </div>}
                  {job.errorCode && (
                    <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 10 }}>
                      {t(`channels.errors.codes.${job.errorCode}`, { defaultValue: t('channels.errors.codes.REQUEST_FAILED') })}
                    </div>
                  )}
                  {job.unmapped > 0 && onReviewUnmapped && (
                    <div style={{ marginTop: 12 }}>
                      <Button kind="outline" size="sm" icon={I.link} onClick={() => onReviewUnmapped(connection.id)} style={mobileActionStyle}>
                        {t('channels.actions.reviewUnmapped')}
                      </Button>
                    </div>
                  )}
                  {job.issueSummaries.length > 0 && (
                    <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('channels.source.issues.title')}
                      </div>
                      {job.issueSummaries.slice(0, 4).map(issue => (
                        <div key={issue.code} style={{ display: 'grid', gap: 2 }}>
                          <div style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 700 }}>
                            {t(`channels.${noonRefresh ? 'noonRefresh' : 'source'}.issues.codes.${issue.code}`, {
                              sku: issue.firstSku ?? t('channels.source.issues.unknownSku'),
                              defaultValue: t('channels.source.issues.codes.UNKNOWN'),
                            })}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.35 }}>
                            {t('channels.source.issues.count', { count: issue.count })}
                            {(issue.firstSku || issue.firstExternalProductId) && (
                              <>
                                {' · '}
                                {t('channels.source.issues.context', {
                                  sku: issue.firstSku ?? t('channels.source.issues.unknownSku'),
                                  externalProductId: issue.firstExternalProductId ?? t('channels.source.issues.unknownExternalProductId'),
                                })}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {job.status === 'FAILED' && (
                    <div style={{ marginTop: 12 }}>
                      <Button kind="outline" size="sm" icon={I.refresh} onClick={() => void retryProductPullJob(job)} disabled={retryingProductJobId === job.id} style={mobileActionStyle}>
                        {retryingProductJobId === job.id ? t('channels.productPull.retrying') : t('channels.productPull.retry')}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {visibleOrderPullJobs.length > 0 && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: visibleOrderPullJobs.some(({ job }) => isOrderPullJobActive(job)) ? 'var(--brand-200)' : 'var(--success-100)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-900)' }}>
                {t('channels.ordersPull.jobsTitle')}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 3 }}>
                {t('channels.ordersPull.jobsSubtitle')}
              </div>
            </div>
            {visibleOrderPullJobs.some(({ job }) => isOrderPullJobActive(job)) && (
              <Pill tone="brand" size="sm" dot>
                {t('channels.ordersPull.activeJobs')}
              </Pill>
            )}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {visibleOrderPullJobs.map(({ job, connection }) => {
              const active = isOrderPullJobActive(job)
              return (
                <div key={job.id} style={{ padding: 12, borderRadius: 12, background: selectedOrderPullJobConnectionId === connection.id ? 'var(--brand-100)' : 'var(--ink-50)', border: `1px solid ${active ? 'var(--brand-200)' : job.status === 'FAILED' ? 'var(--danger-100)' : 'var(--border-subtle)'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                      <ChannelDot name={PROVIDER_DOT_NAMES[connection.provider]} size={24} />
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>
                        {t(active ? 'channels.ordersPull.jobTitle' : 'channels.ordersPull.title', { name: connection.displayName })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Pill tone={onboardingJobTone(job.status)} size="sm" dot>
                        {t(`channels.ordersPull.status.${job.status}`)}
                      </Pill>
                      {!active && (
                        <Button
                          kind="ghost"
                          size="xs"
                          icon={I.x}
                          onClick={() => dismissOrderPullJob(job.id)}
                          ariaLabel={t('channels.ordersPull.dismiss', { name: connection.displayName })}
                          style={{ padding: 6, color: 'var(--fg-muted)' }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(110px, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                    {([
                      ['fetched', job.fetched],
                      ['upserted', job.upserted],
                      ['skipped', job.skipped],
                    ] as const).map(([key, value]) => (
                      <div key={key} style={{ padding: 10, borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)' }}>{value}</div>
                        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{t(`channels.ordersPull.${key}`)}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 10 }}>
                    {t('channels.ordersPull.rangeLabel', { range: orderPullRangeLabel(job) })}
                  </div>
                  {active && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 10 }}>
                      {t('channels.ordersPull.jobQueued')}
                    </div>
                  )}
                  {job.errorCode && (
                    <div style={{ fontSize: 12.5, color: job.status === 'FAILED' ? 'var(--danger)' : 'var(--warning)', marginTop: 10, fontWeight: 700 }}>
                      {t('channels.ordersPull.jobIssue', {
                        message: t(`channels.ordersPull.issues.${job.errorCode}`, { defaultValue: job.errorCode }),
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {loading && (
        <Card padding={22} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('channels.loading')}
          </div>
        </Card>
      )}

      {error !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(error), { defaultValue: String(t('channels.errors.generic')) }))}
          </div>
        </Card>
      )}

      {tableError !== null && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(tableError), { defaultValue: String(t('channels.errors.generic')) }))}
          </div>
        </Card>
      )}

      <Card padding={0}>
        {isMobile ? (
          <div style={{ display: 'grid', gap: 12, padding: 14 }}>
            {!loading && rows.length === 0 && (
              <div style={{ padding: '22px 16px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>{t('channels.empty.title')}</div>
                <div>{t('channels.empty.subtitle')}</div>
              </div>
            )}
            {rows.map(connection => (
              <div key={connection.id} style={{ display: 'grid', gap: 14, padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 14, background: 'white', minWidth: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
                  <ChannelDot name={PROVIDER_DOT_NAMES[connection.provider]} size={34} dim={!connection.adapterAvailable} />
                  <div style={TEXT_WRAP_STYLE}>
                    <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{connection.displayName}</div>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 2 }}>{t(`channels.providers.${connection.provider}`)}</div>
                    {isStockSource(connection) && (
                      <div style={{ marginTop: 3, color: 'var(--fg-subtle)', fontSize: 11.5 }}>
                        {t('channels.source.noPushNote')}
                      </div>
                    )}
                    {connection.errorCode && (
                      <div style={{ marginTop: 3, color: 'var(--danger)', fontFamily: 'var(--font-mono)', fontSize: 11, ...TEXT_WRAP_STYLE }}>
                        {connection.errorCode}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  <div style={{ display: 'grid', gap: 5 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('channels.table.status')}</div>
                    <Pill tone={statusTone(connection.status)} dot size="sm">
                      {t(connection.provider === 'NOON' && connection.status === 'PENDING' && connection.noonSalesPartnerId ? 'channels.noonSalesImport.excelOnly' : `channels.status.${connection.status}`)}
                    </Pill>
                  </div>
                  <div style={{ display: 'grid', gap: 5 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('channels.table.adapter')}</div>
                    <Pill tone={connection.adapterAvailable ? 'success' : 'warning'} size="sm">
                      {connection.adapterAvailable ? t('channels.adapter.available') : t('channels.adapter.comingLater')}
                    </Pill>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('channels.table.source')}</div>
                  {renderConnectionSourceBadges(connection)}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('channels.table.import')}</div>
                  {renderConnectionOnboarding(connection, true)}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('channels.table.webhooks')}</div>
                  {renderConnectionWebhooks(connection, true)}
                </div>
                {renderConnectionActions(connection, true)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ width: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 13 }}>
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: 'var(--ink-50)' }}>
                  {[
                    t('channels.table.channel'),
                    t('channels.table.health'),
                    t('channels.table.activity'),
                    t('channels.table.actions'),
                  ].map((header, index) => (
                    <th key={index} style={{ textAlign: index === 3 ? 'end' : 'start', padding: '12px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '36px 22px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                      <div style={{ fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>{t('channels.empty.title')}</div>
                      <div>{t('channels.empty.subtitle')}</div>
                    </td>
                  </tr>
                )}
                {rows.map(connection => (
                  <tr key={connection.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '18px', verticalAlign: 'top' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', gap: 12, alignItems: 'start' }}>
                        <ChannelDot name={PROVIDER_DOT_NAMES[connection.provider]} size={38} dim={!connection.adapterAvailable} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, color: 'var(--fg)', fontSize: 14, ...TEXT_WRAP_STYLE }}>{connection.displayName}</div>
                          <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 2 }}>{t(`channels.providers.${connection.provider}`)}</div>
                          {isStockSource(connection) && (
                            <div style={{ marginTop: 6, color: 'var(--fg-subtle)', fontSize: 11.5, lineHeight: 1.35, ...TEXT_WRAP_STYLE }}>
                              {t('channels.source.noPushNote')}
                            </div>
                          )}
                          {connection.errorCode && (
                            <div style={{ marginTop: 6, color: 'var(--danger)', fontFamily: 'var(--font-mono)', fontSize: 11, ...TEXT_WRAP_STYLE }}>
                              {connection.errorCode}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '18px', verticalAlign: 'top' }}>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {renderDesktopInfoBlock(t('channels.table.status'), (
                          <Pill tone={statusTone(connection.status)} dot size="sm">
                            {t(connection.provider === 'NOON' && connection.status === 'PENDING' && connection.noonSalesPartnerId ? 'channels.noonSalesImport.excelOnly' : `channels.status.${connection.status}`)}
                          </Pill>
                        ))}
                        {renderDesktopInfoBlock(t('channels.table.adapter'), (
                          <Pill tone={connection.adapterAvailable ? 'success' : 'warning'} size="sm">
                            {connection.adapterAvailable ? t('channels.adapter.available') : t('channels.adapter.comingLater')}
                          </Pill>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '18px', verticalAlign: 'top' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'start' }}>
                        {renderDesktopInfoBlock(t('channels.table.source'), renderConnectionSourceBadges(connection))}
                        {renderDesktopInfoBlock(t('channels.table.import'), renderConnectionOnboarding(connection, true))}
                        {renderDesktopInfoBlock(t('channels.table.webhooks'), renderConnectionWebhooks(connection, true))}
                      </div>
                    </td>
                    <td style={{ padding: '18px', verticalAlign: 'top', textAlign: 'end' }}>
                      {renderConnectionActions(connection, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
